import {
  addToaster,
  DataTypeStore,
  fetchDuplicateRecordsForStagingAPI,
  fetchEnrollmentWithChildCases,
  getEntityDataByEntityFilter,
  getEntityDataByEntityNameApiForFilter,
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  getLayoutByEntityIdAndTypeApi,
  getStagingDedupeStatusData,
  hideLoader,
  LayoutResponseType,
  procedureCallCorrespondingToTenant,
  proceedQuickDeniedCases,
  setUnresolvedDedupeEntities,
  showLoader,
  updateAutomationStagedDetails,
  useTenantNavigate,
} from '@app-store'
import { CustomButton, TableActionType, TabInterface } from '@ui-kit'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { v4 as uuid } from 'uuid'
import { editEntityData } from '../../../apis/entityData.api'
import { DuplicatePopupModal } from '../../../components/workflow/DuplicatePopupModal'
import HybridLayout from '../../../components/hybird-layout/HybridLayout'
import { MULTI_PRODUCT_ENROLLMENT } from '../../../constants/intakeQue.constants'
import { MultiProductPopupModal } from './MultiProductPopupModal'
import {
  FORM_CONSTANTS,
  RECORD_STATUS,
} from '../../../constants/form.constants'
import {
  DUPLICATE_POPUP,
  ENROLLMENT_STATUS,
  ENTITY_ID,
  ENTITY_NAME,
  FILTER_OPERATIONS,
  LAYOUT_NAMES,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
  WORKFLOW_MODAL_TYPES,
} from '../../../utils/constants'
import { DYNAMIC_QUEUE_CONSTANTS } from '../../../constants/dynamicQueue'
import { TOASTER_NAMESPACE } from '../../../constants/addEditPage.strings'
import { ListingTableRow } from '../../../types/listingTable.types'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../../utils/agent.store'

import {
  DuplicatePopupChildCase,
  MultiProductModalType,
} from '../../../utils/types'
import {
  updateEnrollmentStatus,
  updateHasDedupeResolved,
} from './loadCaseWorkflow'
import {
  DataReviewLayoutWrapperProps,
  DataReviewMetaType,
} from '../../../types/multiProduct.types'
import { useParams } from 'react-router-dom'
import {
  clearIntakeStatusSnapshot,
  fetchLatestIntakeStatus,
  getIntakeStatusValue,
  hasIntakeStatusChanged,
  isWebEnrollmentIntake,
  readIntakeStatusSnapshot,
} from '../../../utils/multiProductEnrollment.utils'

export type ChildCaseEnrollmentDetail = DuplicatePopupChildCase

export const DataReviewLayoutWrapper = ({
  childCases,
  faxDetails,
  intakeEntityName,
  communicationId,
  updateStatusToProcessing,
  setFaxDetails,
  onNavigateToIntakeReview,
  refetchEnrollment,
  updateStatusToCompleted,
}: DataReviewLayoutWrapperProps) => {
  const { entityid, layoutid, id } = useParams()
  const dispatch = useAppDispatch()

  const unresolvedDedupeEntities = useAppSelector(
    (state) => state.workflowData?.unresolvedDedupeEntities
  )

  const navigate = useTenantNavigate()

  const [anchorCaseKey, setAnchorCaseKey] = useState<null | string>(null)
  const [tabsList, setTabsList] = useState<TabInterface[]>([])
  const tabsInitializedRef = useRef(false)

  const caseKeyToCaseIdMap = useMemo(() => {
    return childCases.reduce<Record<string, number>>((acc, item) => {
      const caseKey = item.case?.case_key
      const caseId = item.case_id ?? item.case?.id
      if (caseKey && caseId != null) {
        acc[caseKey] = Number(caseId)
      }
      return acc
    }, {})
  }, [childCases])

  const schemaName = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.schema_name
  )
  const userId = useAppSelector((state: RootState) => state.authUser?.user?.id)

  const [recordId, setRecordId] = useState<string>('')
  const [activeModal, setActiveModal] = useState<MultiProductModalType>(null)
  const [updatedIntakeStatus, setUpdatedIntakeStatus] = useState<
    string | undefined
  >()
  const [layout, setLayout] = useState<LayoutResponseType | null>(null)
  const [listingLayout, setListingLayout] = useState<
    LayoutResponseType | undefined
  >()
  const [totalResults, setTotalResults] = useState(0)
  const [modalHandlers, setModalHandlers] = useState<{
    onProceedAnyway?: (isResolved?: boolean) => void
    onCancel?: () => void
    isOpen?: boolean
  }>({})
  const finalDataRef = useRef<FieldValues | null>(null)
  const draftRef = useRef(RECORD_STATUS.ACTIVE)
  const isDraft = useRef<boolean>(false)
  const submitFormButtonRef = useRef<HTMLButtonElement | null>(null)
  const draftSubmitFormButtonRef = useRef<HTMLButtonElement | null>(null)
  const taskMetaRef = useRef<DataReviewMetaType>({})

  const callProceedQuickDeniedCases = async (): Promise<boolean> => {
    if (!communicationId) return false
    try {
      const response = await proceedQuickDeniedCases(communicationId)
      const results = response?.data?.cases ?? []
      return (
        results.length > 0 && results.every((item) => item.quickDenial === true)
      )
    } catch (err) {
      console.error(err)
      return false
    }
  }

  const showAllCasesQuickDeniedModalIfNeeded = async (): Promise<boolean> => {
    const allProceeded = await callProceedQuickDeniedCases()
    if (allProceeded) {
      setActiveModal(WORKFLOW_MODAL_TYPES.ALL_CASES_QUICK_DENIED)
    }
    return allProceeded
  }

  const openPendingIntakeModalIfNeeded = async (commId: string) => {
    const enrollmentId = Number(commId)
    if (Number.isNaN(enrollmentId)) {
      return false
    }

    try {
      const response = await fetchEnrollmentWithChildCases(enrollmentId)
      const cases = response?.data?.hcp_enrollment_details ?? []

      const allRemainingCases = cases.filter(
        (item) => !item?.workflow_proceeded
      )
      if (!allRemainingCases?.length) return false
      const providerSigned = response?.data?.has_provider_sign
      const patientSigned = response?.data?.has_patient_sign

      if (!(providerSigned && patientSigned)) {
        setActiveModal(WORKFLOW_MODAL_TYPES.PENDING_INTAKE)
      } else {
        setActiveModal(WORKFLOW_MODAL_TYPES.INTAKE_COMPLETE)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchStagingRecordIdFromEnrollmentRecord = async () => {
    try {
      const payload = {
        select: [NAMESPACES.ENROLLMENT_ID_KEY],
        entity_name: ENTITY_NAME.CASE_AUTOMATION_STAGING,
        filters: [
          {
            criteria: [
              {
                values: [faxDetails?.enrollment_id ?? ''],
                operation: FILTER_OPERATIONS.EQUALS,
                field_name: NAMESPACES.ENROLLMENT_ID_KEY,
              },
            ],
          },
        ],
        page: 0,
        size: 100,
      }

      const response = await getEntityDataByEntityNameApiForFilter({
        payload,
      })

      const stagingId =
        response?.data?.content?.[0]?.id?.toString() ??
        response?.data?.[0]?.id?.toString() ??
        ''
      setRecordId(stagingId)
      setFaxDetails?.((prev) => {
        if (!prev) return prev
        return { ...prev, staging_id: stagingId }
      })
      return stagingId
    } catch (err) {
      console.error(err)
      return ''
    }
  }

  useEffect(() => {
    if (faxDetails?.enrollment_id) {
      fetchStagingRecordIdFromEnrollmentRecord()
    }
  }, [faxDetails?.enrollment_id])

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const filterPayload = [
          {
            logical_operator: 'AND',
            criteria: [
              {
                values: [ENTITY_NAME.CASE_AUTOMATION_STAGING],
                operation: FILTER_OPERATIONS.EQUALS,
                field_name: 'entity_name',
              },
            ],
          },
        ]
        const payload = {
          entity_name: ENTITY_NAME.LAYOUT,
          select: [
            'type',
            'entity_id',
            'entity_name',
            'status',
            'layout_data',
            'name',
          ],
          page: 0,
          size: 2000,
          filters: filterPayload,
        }
        const response = await getGenericEntityListingAPI(
          payload as unknown as GetGenericEntityListingApiType
        )
        const res = response?.content?.find(
          (item: LayoutResponseType) =>
            item?.type?.toString() ===
              LAYOUT_TYPE_NAMESPACE?.CUSTOM_EDIT_MODE &&
            item?.name === LAYOUT_NAMES?.HYBRID_DATA_REVIEW_LAYOUT
        )

        if (res) {
          setLayout(res)
        }
        taskMetaRef.current = {
          entity_id: res.entity_id,
          entity_name: ENTITY_NAME.CASE_AUTOMATION_STAGING,
          layout_id: res.id?.toString(),
          record_id: recordId,
        }

        const listingRes = await getLayoutByEntityIdAndTypeApi({
          entityId: ENTITY_ID.CASE?.toString(),
          type: LAYOUT_TYPE_NAMESPACE?.LAYOUT_LISTING,
          isDefault: true,
        })
        setListingLayout(listingRes.data?.[0])
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    }
    fetchInfo()
  }, [recordId, dispatch])

  useEffect(() => {
    const fetchInitialAutomationStagingData = async () => {
      if (!recordId) return
      try {
        dispatch(showLoader())
        const res = await getStagingDedupeStatusData(recordId)
        dispatch(updateAutomationStagedDetails(res?.data))
      } catch (err) {
        dispatch(updateAutomationStagedDetails(null))
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    }
    fetchInitialAutomationStagingData()
  }, [recordId, dispatch])

  useEffect(() => {
    return () => {
      dispatch(updateAutomationStagedDetails(null))
      dispatch(setUnresolvedDedupeEntities([]))
    }
  }, [dispatch])

  const isEmptySubFormObject = (obj: {
    _state?: string
    [key: string]: unknown
  }) => {
    if (obj._state === 'REMOVED') return true

    const allowedKeys = ['id', '_state', 'record_status']
    const otherKeysExist = Object.keys(obj).some(
      (k) => !allowedKeys.includes(k)
    )

    return Object.entries(obj).every(([key, value]) => {
      if (!otherKeysExist && key === 'id') {
        return value === null || value === undefined || value === ''
      }

      if (
        otherKeysExist &&
        (key === '_state' || key === 'record_status' || key === 'id')
      )
        return true

      return value === null || value === undefined || value === ''
    })
  }

  const transformFinalData = (data: FieldValues) => {
    const cloned = structuredClone(data)

    Object.keys(cloned).forEach((key) => {
      const value = cloned[key]

      if (!Array.isArray(value)) return
      if (value.length === 0) return
      if (typeof value[0] !== 'object') return

      cloned[key] = value.map((item) => {
        if (!item || typeof item !== 'object') return item

        const shouldRemove = Object.entries(item).every(([k, v]) => {
          if (k === 'id' || k === '_state' || k === 'record_status') return true

          if (
            typeof v === 'object' &&
            isEmptySubFormObject(
              (v as { _state?: string; [key: string]: unknown }) ?? {}
            )
          ) {
            return true
          }

          return (
            v === null ||
            v === undefined ||
            v === '' ||
            (Array.isArray(v) && v.length === 0)
          )
        })
        const { _state, ...rest } = item

        const hasId = 'id' in item && item.id !== null && item.id !== undefined

        if (
          shouldRemove &&
          (_state === NAMESPACES.ADDED ||
            _state === NAMESPACES.ADDED?.toLocaleLowerCase()) &&
          !hasId
        ) {
          return {
            ...rest,
            isOverriddenSubForm: true,
          }
        }

        return item
      })
    })

    return cloned
  }

  const buildChildCaseDuplicatePayload = (activeCaseKey?: string | null) => {
    const entityId = ENTITY_ID.CASE?.toString() ?? ''
    const layoutId = listingLayout?.id?.toString() ?? ''
    const formData = finalDataRef.current
    const patientId = (
      formData?.patient as { id?: number | string } | undefined
    )?.id
    const providerId = (
      formData?.c_provider as { id?: number | string } | undefined
    )?.id
    const facilityId = (
      formData?.c_facilities as { id?: number | string } | undefined
    )?.id
    const requestorId =
      (formData?.requestor as { id?: number | string } | undefined)?.id ??
      faxDetails?.users?.id

    const toEqualsFilter = (value: unknown) => [
      {
        criteria: [
          {
            field_name: 'id',
            operation: FILTER_OPERATIONS.EQUALS,
            values: value != null && value !== '' ? [String(value)] : [],
          },
        ],
      },
    ]

    const filteredCases = activeCaseKey
      ? childCases?.filter(
          ({ case: childCase }) => childCase?.case_key === activeCaseKey
        )
      : childCases?.filter((child) => !child?.workflow_proceeded)

    return filteredCases?.map(({ case: childCase }) => ({
      entity_id: entityId,
      layout_id: layoutId,
      case_key: childCase?.case_key,
      filters: [
        {
          criteria: [
            {
              field_name: 'id',
              operation: FILTER_OPERATIONS.EQUALS,
              values: childCase?.id != null ? [String(childCase.id)] : [],
            },
          ],
        },
      ],
      select: [
        'users',
        'products',
        'patient',
        'c_provider',
        'c_facilities',
        'requestor',
        'services',
        'c_sub_services',
        'programs',
        'id',
        'c_case_statuses',
      ],
      related_entities: {
        users: {
          select: ['id'],
          filters: toEqualsFilter(userId),
        },
        products: {
          select: ['id'],
          filters: toEqualsFilter(childCase?.product_id),
        },
        patient: {
          select: ['id'],
          filters: toEqualsFilter(patientId),
        },
        c_provider: {
          select: ['id'],
          filters: toEqualsFilter(providerId),
        },
        c_facilities: {
          select: ['id'],
          filters: toEqualsFilter(facilityId),
        },
        requestor: {
          select: ['id'],
          filters: toEqualsFilter(requestorId),
        },
        services: {
          select: ['id'],
          filters: toEqualsFilter(childCase?.service_id),
        },
        c_sub_services: {
          select: ['id'],
          filters: toEqualsFilter(childCase?.sub_service_id),
        },
        programs: {
          select: ['id'],
          filters: toEqualsFilter(childCase?.program_id),
        },
        c_case_statuses: {
          select: ['id', 'overall_case_status'],
          filters: [],
        },
      },
    }))
  }

  const fetchTableDataCallback = useCallback(
    async (
      payload?: getEntityDataByEntityFilter['payload'] & { layout_id: number },
      caseKey?: string | null
    ) => {
      if (
        faxDetails?.enrollment_status?.value ===
        ENROLLMENT_STATUS.PENDING_INTAKE
      ) {
        return {
          content: [],
          totalPages: 0,
          totalElements: 0,
        }
      }
      const duplicatePayload = buildChildCaseDuplicatePayload(caseKey)

      const res = await fetchDuplicateRecordsForStagingAPI(
        ENTITY_ID?.CASE ?? 0,
        duplicatePayload,
        payload?.page as number,
        payload?.size as number
      )
      const result = (res.data ?? {}) as Record<
        string,
        {
          content?: ListingTableRow[]
          totalElements?: number
          totalPages?: number
        }
      >
      const duplicateFounds = Object.entries(result).filter(
        ([, data]) => (data?.content?.length ?? 0) > 0
      )

      if (!tabsInitializedRef.current) {
        const nextTabs: TabInterface[] = duplicateFounds.map(([key]) => ({
          label: key,
          key,
          component: null,
          id: caseKeyToCaseIdMap[key],
        }))
        tabsInitializedRef.current = true
        setTabsList(nextTabs)
        setAnchorCaseKey(String(nextTabs?.[0]?.key ?? ''))
      }

      const caseResult =
        (caseKey ? result?.[caseKey] : result?.[duplicateFounds?.[0]?.[0]]) ??
        {}

      if (caseResult?.content?.length) {
        setTotalResults(caseResult?.totalElements ?? 0)
      } else if (
        faxDetails?.enrollment_status?.value ===
        ENROLLMENT_STATUS.CASE_DEDUP_REVIEW
      ) {
        updateEnrollmentStatus(
          communicationId ?? '',
          ENROLLMENT_STATUS.NEEDS_REVIEW
        )
      }
      return {
        content: caseResult?.content ?? [],
        totalPages: caseResult?.totalPages ?? 0,
        totalElements: caseResult?.totalElements ?? 0,
      } as {
        content: ListingTableRow[]
        totalPages: number
        totalElements: number
      }
    },
    [
      childCases,
      listingLayout,
      userId,
      faxDetails?.users?.id,
      faxDetails?.enrollment_status?.value,
      caseKeyToCaseIdMap,
    ]
  )

  useEffect(() => {
    if (!modalHandlers.isOpen) {
      setTabsList([])
    }
  }, [modalHandlers.isOpen])

  const showDuplicateModal = (): Promise<boolean> => {
    return new Promise((resolve) => {
      dispatch(hideLoader())

      const handleProceed = async (isResolved?: boolean) => {
        const allProceeded = await showAllCasesQuickDeniedModalIfNeeded()
        if (allProceeded) {
          setModalHandlers({})
          resolve(true)
          return
        }
        if (isResolved) {
          //In case last case at Dedupe Modal Tabs is attached, update Enrollment Status from Case Dedup Review to Needs Review
          await updateEnrollmentStatus(
            communicationId ?? '',
            ENROLLMENT_STATUS.NEEDS_REVIEW
          )
        }
        await openPendingIntakeModalIfNeeded(communicationId ?? '')
        setModalHandlers({})
        resolve(true)
      }

      const handleCancel = () => {
        setModalHandlers({})
        resolve(false)
      }

      setModalHandlers(() => ({
        onProceedAnyway: handleProceed,
        onCancel: handleCancel,
        isOpen: true,
      }))
    })
  }

  const duplicateActions: TableActionType<DataTypeStore>[] = [
    {
      label: NAMESPACES?.ATTACH,
      key: DUPLICATE_POPUP?.ATTACH_KEY,
      type: DYNAMIC_QUEUE_CONSTANTS.COMPONENT,
      renderActionComponent: (row, rowIndex, onActionClick) => {
        return (
          <button
            type="button"
            className="flex items-center justify-center font-normal text-xs w-[4.313rem] h-[1.75rem] rounded border border-primary-500 hover:bg-primary-100"
            onClick={() =>
              onActionClick(DUPLICATE_POPUP?.ATTACH_KEY, row, rowIndex)
            }
          >
            {NAMESPACES?.ATTACH}
          </button>
        )
      },
    },
  ]

  const buildPayloadForProcedureCall = async (
    data: FieldValues,
    isDraft: boolean
  ) => {
    const payload = {
      params: {
        p_patient_id: {
          type: 'LONGDATATYPE',
          order: 1,
          value: data?.patient?.id,
          is_input_param: true,
        },
        p_provider_id: {
          type: 'LONGDATATYPE',
          order: 2,
          value: data?.c_provider?.id,
          is_input_param: true,
        },
        p_facility_id: {
          type: 'LONGDATATYPE',
          order: 3,
          value: data?.c_facilities?.id,
          is_input_param: true,
        },
        p_is_draft: {
          type: 'BOOLEANDATATYPE',
          order: 4,
          value: isDraft,
          is_input_param: true,
        },
        p_enrollment_id: {
          type: 'STRINGDATATYPE',
          order: 5,
          value: faxDetails?.enrollment_id,
          is_input_param: true,
        },
      },
      procedureName: 'save_or_draft_enrollment_case',
    }
    return payload
  }

  const saveStagingDataToCases = async (
    data: FieldValues,
    isDraft: boolean
  ) => {
    try {
      dispatch(showLoader())
      const payload = await buildPayloadForProcedureCall(data, isDraft)
      await procedureCallCorrespondingToTenant(
        schemaName?.toString() ?? '',
        payload
      )
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const editEntityAndShowToaster = async (
    finalData: FieldValues,
    isDraft: boolean
  ) => {
    try {
      const transformedFinalData = transformFinalData(finalData)

      const response = await editEntityData(
        taskMetaRef?.current?.entity_name ?? '',
        Number(taskMetaRef?.current?.entity_id),
        Number(taskMetaRef?.current?.layout_id),
        transformedFinalData,
        recordId
      )

      if (response?.data) {
        dispatch(
          addToaster({
            id: uuid(),
            type: 'success',
            message: isDraft
              ? TOASTER_NAMESPACE.DRAFT_SAVE_SUCCESS
              : TOASTER_NAMESPACE.SAVE_SUCCESS,
          })
        )
      }

      return response
    } catch (err) {
      console.error(err)

      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: TOASTER_NAMESPACE.FAILED_TO_EDIT_RECORD,
        })
      )

      throw err
    }
  }

  const handleFormSubmit = async (data: FieldValues) => {
    const isDraft = draftRef?.current === RECORD_STATUS.DRAFT

    const finalData: FieldValues = {
      ...data,
      record_status: draftRef?.current,
    }

    finalDataRef.current = finalData

    dispatch(showLoader())

    try {
      await saveStagingDataToCases(data, isDraft)

      await editEntityAndShowToaster(finalData, isDraft)

      if (!isDraft) {
        tabsInitializedRef.current = false

        const duplicateRecords = await fetchTableDataCallback(
          undefined,
          anchorCaseKey
        )

        if ((duplicateRecords?.content?.length ?? 0) > 0) {
          await updateEnrollmentStatus(
            communicationId ?? '',
            ENROLLMENT_STATUS.CASE_DEDUP_REVIEW
          )

          setTotalResults(duplicateRecords?.totalElements ?? 0)

          await showDuplicateModal()
        } else {
          const allProceeded = await showAllCasesQuickDeniedModalIfNeeded()
          if (allProceeded) {
            return
          }

          await updateEnrollmentStatus(
            communicationId ?? '',
            ENROLLMENT_STATUS.NEEDS_REVIEW
          )

          await openPendingIntakeModalIfNeeded(communicationId ?? '')
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleDraftSave = (data: FieldValues) => {
    handleFormSubmit(data)
  }

  const handleSaveButton = () => {
    if (unresolvedDedupeEntities?.length > 0) {
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: NAMESPACES.UNRESOLVED_DEDUPE_ENTITIES_POPUP_HEADER,
        })
      )
      return
    }
    draftRef.current = RECORD_STATUS.ACTIVE
    isDraft.current = false
    submitFormButtonRef?.current?.click()
  }

  const handleSaveDraft = () => {
    draftRef.current = RECORD_STATUS.DRAFT
    isDraft.current = true
    draftSubmitFormButtonRef?.current?.click()
  }

  const handleDuplicateTableActions = (
    actionKey: string,
    row: ListingTableRow
  ) => {
    if (actionKey === DUPLICATE_POPUP?.ATTACH_KEY) {
      void row
    }
  }

  const navigateToQueue = () => {
    clearIntakeStatusSnapshot(communicationId)
    navigate(`/manage-queue/dynamic-queue/${entityid}/${layoutid}/${id}`)
  }

  const handleCancelClick = async () => {
    const stored = readIntakeStatusSnapshot(communicationId)
    const initialStatus = stored?.initialStatus
    let latestStatus = getIntakeStatusValue(faxDetails)
    const isWebEnrollment = isWebEnrollmentIntake(intakeEntityName, faxDetails)

    try {
      dispatch(showLoader())
      const liveStatus = await fetchLatestIntakeStatus(
        communicationId,
        isWebEnrollment
      )
      if (liveStatus) {
        latestStatus = liveStatus
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }

    if (hasIntakeStatusChanged(initialStatus, latestStatus)) {
      setUpdatedIntakeStatus(latestStatus)
      setActiveModal(WORKFLOW_MODAL_TYPES.STATUS_UPDATE_NOTIFICATION)
      return
    }

    navigateToQueue()
  }

  const handleMultiPopupModalConfirm = async () => {
    if (activeModal === WORKFLOW_MODAL_TYPES.INTAKE_COMPLETE) {
      await updateHasDedupeResolved(communicationId ?? '')
      await onNavigateToIntakeReview?.()
    } else if (activeModal === WORKFLOW_MODAL_TYPES.PENDING_INTAKE) {
      await updateEnrollmentStatus(
        communicationId ?? '',
        ENROLLMENT_STATUS.PENDING_INTAKE
      )
      clearIntakeStatusSnapshot(communicationId)
    } else if (activeModal === WORKFLOW_MODAL_TYPES.ALL_CASES_QUICK_DENIED) {
      await updateEnrollmentStatus(
        communicationId ?? '',
        ENROLLMENT_STATUS.COMPLETED
      )
      clearIntakeStatusSnapshot(communicationId)
    } else if (
      activeModal === WORKFLOW_MODAL_TYPES.STATUS_UPDATE_NOTIFICATION
    ) {
      clearIntakeStatusSnapshot(communicationId)
    }
  }

  return (
    <div className="flex flex-col h-full max-h-screen pt-4 pr-2 border border-1 intakeQueReview">
      <div className="flex justify-between items-center px-3 pb-3 border-b">
        <span className="font-medium text-base">
          {MULTI_PRODUCT_ENROLLMENT.DATA_REVIEW}
        </span>
        <div className="flex items-center gap-3">
          <CustomButton onClick={handleCancelClick}>
            {NAMESPACES.CANCEL_BUTTON}
          </CustomButton>
          <CustomButton
            type="button"
            horizontalPaddingClasses="px-5"
            verticalPaddingClasses="py-3"
            onClick={handleSaveDraft}
          >
            {NAMESPACES.SAVE_AS_DRAFT}
          </CustomButton>
          <CustomButton type="button" isFilled onClick={handleSaveButton}>
            {NAMESPACES.SAVE_BUTTON}
          </CustomButton>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pt-2">
        {layout && recordId ? (
          <HybridLayout
            layoutData={layout}
            recordId={recordId}
            isWorkflow={true}
            mode="edit"
            openAllAccordions
            isDraft={isDraft}
            handleFormSubmit={handleFormSubmit}
            handleDraftSave={handleDraftSave}
            formHeaders={
              <>
                <CustomButton
                  className="hidden"
                  isFilled
                  ref={submitFormButtonRef}
                  type="submit"
                >
                  button
                </CustomButton>
                <CustomButton
                  id={FORM_CONSTANTS?.SAVE_AS_DRAFT}
                  className="hidden"
                  isFilled
                  ref={draftSubmitFormButtonRef}
                  type="submit"
                >
                  button
                </CustomButton>
              </>
            }
          />
        ) : null}
      </div>

      {modalHandlers.isOpen && (
        <DuplicatePopupModal
          isOpen={modalHandlers.isOpen ?? false}
          title={DUPLICATE_POPUP.DUPLICATES_FOUND}
          totalResults={totalResults}
          dataFetchCallback={fetchTableDataCallback}
          entityId={ENTITY_ID.HCP_PORTAL_ENTITY}
          entityName={taskMetaRef?.current?.entity_name ?? ''}
          layout={listingLayout}
          extraActions={duplicateActions?.map((actionConfig) => ({
            ...actionConfig,
            onClick: (row: ListingTableRow) =>
              handleDuplicateTableActions(actionConfig.key, row),
          }))}
          onProceedAnyway={modalHandlers.onProceedAnyway}
          onCancel={() => {
            setModalHandlers({})
            setAnchorCaseKey(null)
          }}
          updateStatusToCompleted={updateStatusToCompleted}
          communicationId={communicationId}
          updateStatusToProcessing={updateStatusToProcessing}
          faxDetails={faxDetails}
          intakeEntityName={intakeEntityName}
          isMultiCaseEnabled={true}
          childCases={childCases}
          tabsList={tabsList}
          activeChildCaseId={anchorCaseKey}
          onChildCaseTabChange={setAnchorCaseKey}
          onRemoveChildCaseTab={setTabsList}
          closeModalOnOutsideClick={false}
          refetchEnrollment={refetchEnrollment}
        />
      )}
      {activeModal && (
        <MultiProductPopupModal
          activeModal={activeModal}
          latestStatus={updatedIntakeStatus}
          closeModal={() => {
            setAnchorCaseKey(null)
            setActiveModal(null)
            setUpdatedIntakeStatus(undefined)
          }}
          onConfirmClick={handleMultiPopupModalConfirm}
        />
      )}
    </div>
  )
}

export default DataReviewLayoutWrapper
